import type { ReactElement } from "react";

import type { IconName } from "@similarweb/icons";
import { Flex } from "@similarweb/ui-components/foundations";

import { formatMoney } from "app/pages/sales-intelligence/modules/leads-search-filters/utils/numberFormatters";
import { formatTaxonomyPathDisplayNames } from "app/pages/sales-intelligence/utils/taxonomy-tree/path";
import { is } from "app/pages/sales-intelligence/utils/type-guards";
import countryService from "app/services/CountryService";
import { UNKNOWN_COUNTRY_ID } from "app/services/countryServiceConst";

import type { ITopCompanyItem } from "../../types/TopCompany";

import { StyledTopCompanyInfoIcon, StyledTopCompanyInfoItem, StyledTopCompanyInfoItemText } from "./styles";

type ChipItem = { icon: IconName; text: string };

type Props = Pick<ITopCompanyItem, "annualRevenue" | "companyCountryCode" | "industry" | "industryTaxonomy">;

const TopCompanyInfoItems = ({
    industry,
    industryTaxonomy,
    companyCountryCode = UNKNOWN_COUNTRY_ID,
    annualRevenue,
}: Props): ReactElement => {
    const country = countryService.getCountryById(companyCountryCode);

    const formattedAnnualRevenue = is.number(annualRevenue) ? formatMoney(annualRevenue) : annualRevenue;

    // Which classification the chip speaks (ADR-0013): the backend serves one or the other, never both.
    const industryText = industryTaxonomy ? formatTaxonomyPathDisplayNames(industryTaxonomy.path) : industry;

    const chipsList = [
        {
            icon: "group-industry",
            text: industryText,
        },
        {
            icon: "revenue",
            text: formattedAnnualRevenue || null,
        },
        {
            icon: "location",
            text: country?.text,
        },
    ].filter(({ text }) => text) as readonly ChipItem[];

    return (
        <Flex
            gap="6px"
            flexWrap="wrap"
        >
            {chipsList.map(({ icon, text }) => (
                <StyledTopCompanyInfoItem>
                    <StyledTopCompanyInfoIcon
                        iconName={icon}
                        size="xs"
                    />
                    <StyledTopCompanyInfoItemText fontVariant="primaryItem">{text}</StyledTopCompanyInfoItemText>
                </StyledTopCompanyInfoItem>
            ))}
        </Flex>
    );
};

export default TopCompanyInfoItems;
